/** Continuous timber crossings on the tactical grid, with a shallow raised deck. */
import type { Graphics } from 'pixi.js';
import type { TileMap } from '../sim/map.ts';
import { Terrain } from '../sim/types.ts';
import { shade } from '../data/palette.ts';

export const BRIDGE_RISE = 0.14;
export interface BridgeTileEdges {
  /** Direction of travel, not the direction of the cross-deck planks. */
  vertical: boolean;
  start: boolean;
  end: boolean;
  /** Outer sides only: adjacent lanes must never have an internal railing. */
  left: boolean;
  right: boolean;
  variant: number;
}
type Point = [number, number];

/** Derive the axis from the deck and its landings. Every decor bit may contain
 * random variation in generated maps and old saves, so none is an axis flag. */
export function bridgeVertical(map: TileMap, x: number, y: number): boolean {
  const length = (dx: number, dy: number): number => {
    let score = 0;
    for (const sign of [-1, 1]) for (let d = 1; d <= 32; d++) {
      const nx = x + dx * d * sign, ny = y + dy * d * sign;
      if (!map.inBounds(nx, ny)) break;
      const t = map.terrain[map.idx(nx, ny)];
      if (t !== Terrain.Bridge) { if (t === Terrain.Road) score += 0.75; break; }
      score++;
    }
    return score;
  };
  return length(0, 1) > length(1, 0);
}

export function bridgeTileEdges(map: TileMap, x: number, y: number): BridgeTileEdges {
  const vertical = bridgeVertical(map, x, y);
  const dx = vertical ? 0 : 1, dy = vertical ? 1 : 0;
  const isBridge = (tx: number, ty: number) => map.inBounds(tx, ty) && map.terrain[map.idx(tx, ty)] === Terrain.Bridge;
  const dry = (tx: number, ty: number) => {
    if (!map.inBounds(tx, ty)) return false;
    const t = map.terrain[map.idx(tx, ty)];
    return t !== Terrain.Bridge && t !== Terrain.River && t !== Terrain.Deep;
  };
  return {
    vertical, start: dry(x - dx, y - dy), end: dry(x + dx, y + dy),
    left: !isBridge(x - dy, y - dx), right: !isBridge(x + dy, y + dx),
    variant: map.decor[map.idx(x, y)] & 3,
  };
}

/** Called in the structure layer; the optional near rails sort above walkers. */
export function drawBridgeTile(
  g: Graphics, sx: number, sy: number, z: number, edges: BridgeTileEdges, foreground?: Graphics,
): void {
  const { vertical, start, end, left, right, variant } = edges;
  const at = (along: number, across: number, height = 0): Point => vertical
    ? [sx + across * z, sy + (along - height) * z]
    : [sx + along * z, sy + (across - height) * z];
  const poly = (target: Graphics, points: Point[], color: number, alpha = 1) => target.poly(points.flat()).fill({ color, alpha });
  const stroke = (target: Graphics, a: Point, b: Point, color: number, width: number, alpha = 1) =>
    target.moveTo(...a).lineTo(...b).stroke({ color, width: Math.max(0.55, z * width), alpha, cap: 'butt' });
  const box = (target: Graphics, a: number, c: number, length: number, width: number, bottom: number, height: number, color: number) => {
    const top = bottom + height;
    const corners = [at(a, c, bottom), at(a + length, c, bottom), at(a + length, c + width, bottom), at(a, c + width, bottom)];
    const lid = [at(a, c, top), at(a + length, c, top), at(a + length, c + width, top), at(a, c + width, top)];
    poly(target, [lid[1], lid[2], corners[2], corners[1]], shade(color, 0.61));
    poly(target, [lid[2], lid[3], corners[3], corners[2]], shade(color, 0.78));
    poly(target, lid, color);
  };
  const lo = left ? 0.055 : 0, hi = right ? 0.945 : 1;
  poly(g, [at(-0.025, lo + 0.08), at(1.025, lo + 0.08), at(1.025, hi + 0.12), at(-0.025, hi + 0.12)], 0x101c20, 0.28);

  // Squat masonry bearings sit on the two banks; intermediate pieces have no
  // imitation stone doorstep or rail across the direction of travel.
  for (const a of [start ? -0.16 : NaN, end ? 0.84 : NaN]) {
    if (!Number.isFinite(a)) continue;
    box(g, a, lo - 0.05, 0.32, hi - lo + 0.1, -0.015, BRIDGE_RISE - 0.01, 0xa2a797);
    stroke(g, at(a + 0.08, lo - 0.02, 0.035), at(a + 0.08, hi + 0.02, 0.035), 0x626f69, 0.016, 0.8);
  }
  // The long beams are continuous across tile joins. Only the outer girder is
  // visible; adjoining road lanes share an uninterrupted plank surface.
  for (const c of [left ? 0.065 : NaN, right ? 0.89 : NaN]) {
    if (!Number.isFinite(c)) continue;
    box(g, -0.005, c, 1.01, 0.05, 0.015, BRIDGE_RISE - 0.02, 0x796346);
  }
  poly(g, [at(-0.005, lo, BRIDGE_RISE), at(1.005, lo, BRIDGE_RISE), at(1.005, hi, BRIDGE_RISE), at(-0.005, hi, BRIDGE_RISE)], 0xa58c62);
  const planks = z < 15 ? 3 : 6;
  for (let i = 0; i < planks; i++) {
    const a = i / planks, b = (i + 1) / planks;
    poly(g, [at(a, lo, BRIDGE_RISE), at(b, lo, BRIDGE_RISE), at(b, hi, BRIDGE_RISE), at(a, hi, BRIDGE_RISE)],
      [0xa58c62, 0xb19a70, 0x95815e, 0xa78e64][(i + variant) % 4]);
    stroke(g, at(a, lo, BRIDGE_RISE), at(a, hi, BRIDGE_RISE), 0x544c3b, 0.012, 0.65);
    if (z > 24 && i % 2 === 0) stroke(g, at(a + 0.035, lo + 0.1, BRIDGE_RISE), at(a + 0.035, hi - 0.1, BRIDGE_RISE), 0xd4bf94, 0.008, 0.4);
  }
  const railing = (c: number, near: boolean) => {
    const target = near && foreground ? foreground : g;
    const railTop = BRIDGE_RISE + 0.31;
    box(target, -0.02, c, 1.04, 0.045, railTop - 0.045, 0.045, 0xb39a6a);
    if (z >= 15) {
      box(target, -0.005, c + 0.005, 1.01, 0.03, BRIDGE_RISE + 0.11, 0.035, 0x957b52);
      for (const a of [start ? 0.025 : NaN, 0.5, end ? 0.975 : NaN]) {
        if (!Number.isFinite(a)) continue;
        box(target, a - 0.025, c - 0.005, 0.055, 0.055, 0.015, railTop + 0.015, 0xc0a579);
        stroke(target, at(a, c + 0.052, BRIDGE_RISE + 0.025), at(a, c + 0.052, railTop - 0.07), 0x524938, 0.011);
      }
    }
  };
  if (left) railing(0.045, vertical);
  if (right) railing(0.91, true);
}
